import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  useTransactionDistribution,
  TimePeriod,
  CategoryDistribution,
} from '@/hooks/useTransactionDistribution';
import { formatCurrency } from '@/lib/currency.utils';

interface TransactionDistributionChartProps {
  timePeriod: TimePeriod;
  chartType: 'pie' | 'bar';
  transactionType: 'income' | 'expense';
  currency?: string;
}

/**
 * Custom tooltip for the distribution chart
 */
const CustomTooltip = ({
  active,
  payload,
  currency = 'USD',
}: {
  active?: boolean;
  payload?: Array<{ payload: CategoryDistribution }>;
  currency?: string;
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload as CategoryDistribution;
    return (
      <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
        <p className="font-semibold text-sm mb-1">
          {data.categoryIcon} {data.categoryName}
        </p>
        <p className="text-sm text-muted-foreground">
          Amount: {formatCurrency(data.total, currency)}
        </p>
        <p className="text-sm text-muted-foreground">
          Percentage: {data.percentage.toFixed(1)}%
        </p>
        <p className="text-sm text-muted-foreground">
          Transactions: {data.transactionCount}
        </p>
      </div>
    );
  }
  return null;
};

/**
 * Transaction Distribution Chart Component
 * Displays pie chart showing percentage distribution of transactions by category
 * Uses useTransactionDistribution hook for data aggregation
 */
export function TransactionDistributionChart({
  timePeriod,
  chartType,
  transactionType,
  currency = 'USD',
}: TransactionDistributionChartProps) {
  const { data, isLoading, error } = useTransactionDistribution({
    timePeriod,
    transactionType,
  });

  const title =
    transactionType === 'income' ? 'Income Distribution' : 'Expense Distribution';
  const description = `${transactionType === 'income' ? 'Income' : 'Expense'} breakdown by category`;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>Loading {transactionType} distribution...</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[300px]">
          <div className="flex flex-col items-center gap-3">
            <div className="h-12 w-12 rounded-full border-4 border-primary border-t-transparent animate-spin" />
            <p className="text-sm text-muted-foreground">Loading chart data...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-destructive">Error loading data: {error.message}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-muted-foreground">No data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div
          role="img"
          aria-label={`${transactionType} distribution pie chart showing ${data.length} categories`}>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart tabIndex={0} aria-label={`${transactionType} distribution chart`}>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ percentage }) => `${percentage.toFixed(1)}%`}
                outerRadius={80}
                fill="hsl(var(--primary))"
                dataKey="total">
                {data.map((entry) => (
                  <Cell key={`cell-${entry.categoryId}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip currency={currency} />} />
              <Legend
                formatter={(value, entry: { payload: CategoryDistribution }) => {
                  const payload = entry.payload;
                  return `${payload.categoryIcon} ${payload.categoryName}`;
                }}
                wrapperStyle={{ fontSize: '12px' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        {/* Screen reader accessible data table */}
        <div className="sr-only">
          <table>
            <caption>{title} data</caption>
            <thead>
              <tr>
                <th>Category</th>
                <th>Amount</th>
                <th>Percentage</th>
                <th>Transaction Count</th>
              </tr>
            </thead>
            <tbody>
              {data.map((entry) => (
                <tr key={entry.categoryId}>
                  <td>
                    {entry.categoryIcon} {entry.categoryName}
                  </td>
                  <td>{formatCurrency(entry.total, currency)}</td>
                  <td>{entry.percentage.toFixed(1)}%</td>
                  <td>{entry.transactionCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
